import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { ONBOARDING_REDIRECT_TO_AFTER_COMPLETE_KEY } from '@/constants/router';
import { useProfileInstitutionDetailsAnswers } from '@/hooks/queries/use-profile-institution-details';
import { useProfileVerificationInformation } from '@/hooks/queries/use-profile-verification-information';
import LoadingPage from '@/pages/loading';
import type { OnboardedGuardVerificationPathType } from '@/pages/onboarded-guard.types';

/**
 * Note: The "/institution/onboarding/verification/status" route has its own verification path. As such, we don't want
 * to redirect back to onboarding if the user is on this path AND the information they're missing corresponds to the
 * current path. If information other than verification is missing (or the missing information does not correspond to the
 * current path), we'll still redirect back.
 */

type InstitutionOnboardedGuardProps = {
  redirectToAfterComplete?: string;
  verificationPaths?: Partial<Record<OnboardedGuardVerificationPathType, string>>;
};

const InstitutionOnboardedGuard = ({ redirectToAfterComplete, verificationPaths }: InstitutionOnboardedGuardProps) => {
  const { isCompleteOnboarding: isCompleteInstitutionDetailsOnboarding, isLoading: isLoadingInstitutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { isLoading: isLoadingVerificationInformation, data: verificationInformation } = useProfileVerificationInformation();
  const location = useLocation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingVerificationInformation;
  const isCompleteVerificationInformation = !!verificationInformation?.verification && verificationInformation.verification.status !== 'NOT_VERIFIED';
  const isOnboardedExcludingVerification = isCompleteInstitutionDetailsOnboarding;
  const isOnboardedVerification = isCompleteVerificationInformation;
  const shouldRedirect =
    !isOnboardedExcludingVerification ||
    (!verificationPaths ? !isOnboardedVerification : !isCompleteVerificationInformation && location.pathname !== verificationPaths.VERIFICATION);

  if (isLoading) return <LoadingPage />;

  if (shouldRedirect) {
    if (redirectToAfterComplete) {
      window.sessionStorage.setItem(ONBOARDING_REDIRECT_TO_AFTER_COMPLETE_KEY, redirectToAfterComplete);
    }

    return <Navigate to="/institution/onboarding" replace />;
  }

  return <Outlet />;
};
InstitutionOnboardedGuard.displayName = 'InstitutionOnboardedGuard';

export default InstitutionOnboardedGuard;
